import { LatencySite, SiteLatencyEntry, SiteLatencyMatrixData } from "./request";

export interface DirectedSiteLatency {
    rttUs: number;
    /** One-way delay from the row site to the column site. */
    outboundUs: number;
    /** One-way delay back from the column site to the row site. */
    inboundUs: number;
    baselineRttUs: number | null;
}

/** A pair's latency by site id, in either order; null when it is not measured. */
export type SiteLatencyLookup = (
    row: string,
    col: string
) => DirectedSiteLatency | null;

/**
 * The API sends each unordered pair once (`from` < `to`). The table shows
 * both halves, so a lookup reads either orientation and flips the one-way
 * values when the row is the entry's `to` side.
 */
export function buildSiteLatencyLookup(
    data: SiteLatencyMatrixData
): SiteLatencyLookup {
    const byPair = new Map<string, SiteLatencyEntry>();
    for (const entry of data.matrix) {
        byPair.set(`${entry.from}|${entry.to}`, entry);
    }

    return (row, col) => {
        const direct = byPair.get(`${row}|${col}`);
        if (direct) {
            return {
                rttUs: direct.rttUs,
                outboundUs: direct.oneWayUs.forward,
                inboundUs: direct.oneWayUs.reverse,
                baselineRttUs: direct.baselineRttUs,
            };
        }
        const flipped = byPair.get(`${col}|${row}`);
        if (!flipped) return null;
        return {
            rttUs: flipped.rttUs,
            outboundUs: flipped.oneWayUs.reverse,
            inboundUs: flipped.oneWayUs.forward,
            baselineRttUs: flipped.baselineRttUs,
        };
    };
}

/** "Oct 23, 17:00" for the snapshot's `measuredAt`; null when absent or unreadable. */
export function formatSnapshotTime(measuredAt: string | null): string | null {
    if (!measuredAt) return null;
    const time = new Date(measuredAt);
    if (Number.isNaN(time.getTime())) return null;
    return time.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
}

/** "17:20" for the snapshot's `measuredAt`: the legend's short form. */
export function formatSnapshotClock(measuredAt: string | null): string | null {
    if (!measuredAt) return null;
    const time = new Date(measuredAt);
    if (Number.isNaN(time.getTime())) return null;
    return time.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
}

/**
 * Sites with no backbone router of their own, by the backbone site they hang
 * from. The matrix has no row for them (no IS-IS node, no delay to read), so
 * a DCI that lands there reads the RTT of that site instead. Confirmed with
 * José Ygnacio on 2026-10-23 for Triara QRO.
 */
const LATENCY_ANCHOR_SITE: Readonly<Record<string, string>> = {
    TRIQRO: "KIOQRO1",
};

/** The matrix site whose RTT a datacenter reads: itself, or its anchor. */
export function latencySiteOf(datacenterId: string): string {
    return LATENCY_ANCHOR_SITE[datacenterId] ?? datacenterId;
}

/**
 * The lowest RTT among the given site pairs, or null when none is measured.
 * The Overview draws several DCIs as one line between two pins; the line
 * reads the fastest of them, the same "lowest" rule the matrix uses per
 * site pair.
 */
export function lowestRttUs(
    lookup: SiteLatencyLookup,
    pairs: [string, string][]
): number | null {
    let lowest: number | null = null;
    for (const [from, to] of pairs) {
        const latency = lookup(from, to);
        if (latency && (lowest === null || latency.rttUs < lowest)) {
            lowest = latency.rttUs;
        }
    }
    return lowest;
}

/** "KIO QRO1 (Queretaro, MX)" → "KIO QRO1": the axis stays narrow. */
export function shortSiteLabel(label: string): string {
    const open = label.indexOf(" (");
    return open > 0 ? label.slice(0, open) : label;
}

/**
 * "KIO QRO1 (Queretaro, MX)" → { provider: "KIO", site: "QRO1" }, so the
 * matrix headers can stack the two and stay narrow. A one-word label has
 * no provider line.
 */
export function siteLabelParts(label: string): {
    provider: string | null;
    site: string;
} {
    const short = shortSiteLabel(label);
    const lastSpace = short.lastIndexOf(" ");
    if (lastSpace <= 0) return { provider: null, site: short };
    return {
        provider: short.slice(0, lastSpace),
        site: short.slice(lastSpace + 1),
    };
}

export interface SiteNeighbour {
    site: LatencySite;
    rttUs: number;
}

export interface SiteNeighbours {
    site: LatencySite;
    closest: SiteNeighbour | null;
    farthest: SiteNeighbour | null;
}

/** For every site, the other site it reaches fastest and slowest. */
export function siteNeighbours(data: SiteLatencyMatrixData): SiteNeighbours[] {
    const lookup = buildSiteLatencyLookup(data);
    return data.sites.map((site) => {
        let closest: SiteNeighbour | null = null;
        let farthest: SiteNeighbour | null = null;
        for (const other of data.sites) {
            if (other.id === site.id) continue;
            const latency = lookup(site.id, other.id);
            if (!latency) continue;
            const candidate = { site: other, rttUs: latency.rttUs };
            if (!closest || candidate.rttUs < closest.rttUs) closest = candidate;
            if (!farthest || candidate.rttUs > farthest.rttUs) farthest = candidate;
        }
        return { site, closest, farthest };
    });
}
